import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import styles from "./TOIAboundComparisonLandingPage.module.css";
import "../../../assets/css/toi/common.css";
import TOIReminderSheet from "./TOIReminderSheet";
import {
  toiLandingContinueClick,
  toiLandingPageLanded,
} from "../../../libs/events";
import {
  TOI_WELCOME_BONUS,
  TOI_WELCOME_BONUS_INR,
} from "../../../constants";
import {
  competitorFxRates,
  nonLoggedInExchangeRate,
} from "../../../services/remittance";
import {
  DEFAULT_USD_AMOUNT,
  formatInr,
  formatUsd,
  getRateViewModel,
  toNumber,
} from "../../GetAppLanding/components/rateUtils";
import toiLogoDark from "../../../assets/img/toi/updatedLanding/toi-logo-dark.svg";
import toiLogoLight from "../../../assets/img/toi/updatedLanding/toi-logo-light.svg";
import aboundLogoDark from "../../../assets/img/toi/updatedLanding/abound-logo-dark.svg";
import aboundLogoLight from "../../../assets/img/toi/updatedLanding/abound-logo-light.svg";
import USFlag from "../../../assets/img/toi/updatedLanding/us-flag.svg";
import IndiaFlag from "../../../assets/img/toi/updatedLanding/india-flag.svg";
import rewardGiftIcon from "../../../assets/img/toi/updatedLanding/gift.svg";
import featureUsers from "../../../assets/img/toi/aboundComparison/feature-users.svg";
import featureFlash from "../../../assets/img/toi/aboundComparison/feature-flash.svg";
import featureShield from "../../../assets/img/toi/aboundComparison/feature-shield.svg";
import googleSymbol from "../../../assets/img/toi/aboundComparison/google-symbol.svg";
import wiseSymbol from "../../../assets/img/toi/aboundComparison/wise-symbol.svg";
import remitlySymbol from "../../../assets/img/toi/aboundComparison/remitly-symbol.svg";
import trendArrow from "../../../assets/img/toi/aboundComparison/trend-arrow.svg";
import aboundMarkLeft from "../../../assets/img/toi/aboundComparison/abound-mark-left.svg";
import aboundMarkBottom from "../../../assets/img/toi/aboundComparison/abound-mark-bottom.svg";
import aboundMarkRight from "../../../assets/img/toi/aboundComparison/abound-mark-right.svg";

const FAQS = [
  {
    question: "What is Abound?",
    answer:
      "Abound is a financial app built for Indian NRIs in the US. We offer the best FX rates to India, cashback on transfers, and tools to help you manage your money across borders — all in one place.",
  },
  {
    question: `Is the ${TOI_WELCOME_BONUS} really mine?`,
    answer:
      "Yes — it's a welcome bonus credited to your Abound wallet with absolutely no restrictions. Use it to send money to India, no minimum required.",
  },
  {
    question: "How long does a transfer take?",
    answer:
      "Your transaction to India will mostly be settled on the same day. You'll get a notification the moment your money lands.",
  },
  {
    question: "Can I withdraw it instead of sending it?",
    answer:
      "No, You can’t withdraw directly. The $50 welcome bonus is applied directly to your first transfer to India. It reduces the amount you need to send out of pocket — so you send less, your family receives more.",
  },
  {
    question: "Are there any fees or minimums?",
    answer:
      "No hidden fees and in fact you will get a free first transfer. Abound shows you the exact amount your recipient gets before you confirm. There's no minimum transfer amount to get started.",
  },
];

const FEATURES = [
  { label: "App built for NRIs", icon: featureUsers },
  { label: "Faster transfers", icon: featureFlash },
  { label: "Best FX rates", icon: featureShield },
];

const RATE_PREVIEW = "$1 = ₹105";
const LANDING_PAGE_TYPE = "landing_page_abound_comparison";
const EXPERIMENT_VARIANT = "abound_comparison";

const COMPARISON_PROVIDER_ORDER = ["Google", "Wise", "Remitly"];
const LOCAL_PROVIDER_ICONS = {
  google: googleSymbol,
  wise: wiseSymbol,
  remitly: remitlySymbol,
};

const normalizeProvider = (provider) => ({
  name: provider.provider_name || provider.name,
  logo:
    provider.logo ||
    provider.light_logo_url ||
    provider.dark_logo_url ||
    provider.logoUrl,
  rate: toNumber(provider.fx_rate || provider.rate),
  fee: toNumber(provider.transfer_fee || provider.fee),
});

const getRecipientAmount = (row, amount) => {
  const sentAfterFee = Math.max(amount - row.fee, 0);
  return sentAfterFee * row.rate;
};

const formatTableInr = (value) => formatInr(Math.round(toNumber(value)));

const AboundMark = () => (
  <span className={styles.aboundMark} aria-hidden>
    <img src={aboundMarkLeft} alt="" className={styles.aboundMarkLeft} />
    <img src={aboundMarkBottom} alt="" className={styles.aboundMarkBottom} />
    <img src={aboundMarkRight} alt="" className={styles.aboundMarkRight} />
  </span>
);

function TOIAboundComparisonLandingPage() {
  const navigate = useNavigate();
  const [theme, setTheme] = useState(() => {
    const stored = window.sessionStorage.getItem("theme") || "";
    return stored || "light";
  });
  const landingEventFired = useRef(false);
  const viewAppearTsRef = useRef(null);
  const hasUserInteracted = useRef(false);
  const faqItemRefs = useRef([]);
  const shouldScrollOpenedFaq = useRef(false);
  const [openFaqIndex, setOpenFaqIndex] = useState(0);
  const [exchangeData, setExchangeData] = useState(null);
  const [apiCompetitors, setApiCompetitors] = useState([]);

  const isLight = (theme || "").toLowerCase() === "light";
  const rateModel = useMemo(
    () => getRateViewModel(exchangeData || {}, DEFAULT_USD_AMOUNT),
    [exchangeData],
  );
  const hasExchangeData = Boolean(exchangeData);
  const comparisonRows = useMemo(() => {
    const apiRows = apiCompetitors
      .map(normalizeProvider)
      .filter(
        (row) =>
          row.name && row.rate > 0 && row.rate <= rateModel.blendedRate,
      )
      .map((row) => {
        const recipient = getRecipientAmount(row, rateModel.amount);
        const providerKey = row.name.toLowerCase();

        return {
          provider: row.name,
          amount: formatTableInr(recipient),
          recipient,
          icon: LOCAL_PROVIDER_ICONS[providerKey] || row.logo,
        };
      });

    const orderedRows = COMPARISON_PROVIDER_ORDER.map((providerName) =>
      apiRows.find(
        (row) => row.provider.toLowerCase() === providerName.toLowerCase(),
      ),
    ).filter(Boolean);

    return orderedRows.length ? orderedRows : apiRows.slice(0, 3);
  }, [apiCompetitors, rateModel.amount, rateModel.blendedRate]);
  const hasComparisonData = hasExchangeData && comparisonRows.length > 0;
  const aboundRecipientAmount = rateModel.recipientAmount;
  const bestCompetitorAmount = Math.max(
    ...comparisonRows.map((row) => toNumber(row.recipient)),
    0,
  );
  const extraAmount = Math.max(aboundRecipientAmount - bestCompetitorAmount, 0);
  useEffect(() => {
    const stored = window.sessionStorage.getItem("theme") || "light";
    setTheme(stored);
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    nonLoggedInExchangeRate({ signal: controller.signal })
      .then((data) => {
        if (data) {
          setExchangeData(data);
        }
      })
      .catch((error) => {
        if (error?.name !== "CanceledError") {
          console.log(error);
        }
      });

    competitorFxRates({ signal: controller.signal })
      .then((providers) => {
        setApiCompetitors(providers);
      })
      .catch((error) => {
        if (error?.name !== "CanceledError") {
          console.log(error);
        }
      });

    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (openFaqIndex === null || !shouldScrollOpenedFaq.current) {
      return undefined;
    }

    const scrollTimer = setTimeout(() => {
      const openedFaq = faqItemRefs.current[openFaqIndex];
      if (!openedFaq || typeof window === "undefined") return;

      const footerSafeArea = 120;
      const rect = openedFaq.getBoundingClientRect();
      const visibleBottom = window.innerHeight - footerSafeArea;
      const isFullyVisible = rect.top >= 0 && rect.bottom <= visibleBottom;

      if (!isFullyVisible) {
        openedFaq.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
      shouldScrollOpenedFaq.current = false;
    }, 80);

    return () => clearTimeout(scrollTimer);
  }, [openFaqIndex]);

  useEffect(() => {
    const isIosWebView =
      typeof window !== "undefined" &&
      typeof window.webkit?.messageHandlers?.tilAppWebBridge !== "undefined";

    const fireLandingEvent = (source, options = {}) => {
      const force = options?.force === true;
      if (document.visibilityState !== "visible") return;
      if (!force) {
        if (landingEventFired.current) return;
        landingEventFired.current = true;
      }
      toiLandingPageLanded({
        utm_source: "toi_mobile",
        utm_medium: "toi_mobile",
        utm_campaign: "toi_mobile",
        trigger_source: source,
        view_appear_ts: viewAppearTsRef.current || undefined,
        landing_page_type: LANDING_PAGE_TYPE,
        experiment_variant: EXPERIMENT_VARIANT,
      });
    };

    const onMessage = (evt) => {
      try {
        let payload = evt?.data;
        if (!payload) return;
        if (typeof payload === "string") payload = JSON.parse(payload);
        if (payload?.eventType === "viewAppear") {
          viewAppearTsRef.current = payload?.timestamp || Date.now();
          fireLandingEvent("ios_viewAppear", { force: true });
        }
      } catch (e) {}
    };

    const onInteraction = () => {
      if (hasUserInteracted.current) return;
      hasUserInteracted.current = true;
      fireLandingEvent("user_interaction");
    };

    const interactionEvents = ["click", "touchstart", "keydown"];
    interactionEvents.forEach((event) => {
      window.addEventListener(event, onInteraction, { once: true });
    });

    if (isIosWebView) {
      window.addEventListener("message", onMessage);
      return () => {
        interactionEvents.forEach((event) =>
          window.removeEventListener(event, onInteraction),
        );
        window.removeEventListener("message", onMessage);
      };
    }

    fireLandingEvent("land");
    return () => {
      interactionEvents.forEach((event) =>
        window.removeEventListener(event, onInteraction),
      );
    };
  }, []);

  const handleContinue = () => {
    toiLandingContinueClick({
      click_source: LANDING_PAGE_TYPE,
      experiment_variant: EXPERIMENT_VARIANT,
    });
    setTimeout(() => navigate("/onboarding/signup"), 50);
  };

  const handleFaqToggle = (index) => {
    shouldScrollOpenedFaq.current = true;
    setOpenFaqIndex((currentIndex) => (currentIndex === index ? null : index));
  };

  return (
    <div className={styles.container} data-theme={isLight ? "light" : "dark"}>
      <header className={styles.brandBar}>
        <div
          className={styles.brandLockup}
          aria-label="The Times of India x Abound"
        >
          <img
            src={isLight ? toiLogoLight : toiLogoDark}
            alt="The Times of India"
            className={styles.toiLogo}
          />
          <span className={styles.brandCross}>x</span>
          <img
            src={isLight ? aboundLogoLight : aboundLogoDark}
            alt="Abound"
            className={styles.aboundLogo}
          />
        </div>
      </header>

      <main className={styles.mainContent}>
        <section className={styles.heroIntro}>
          <h1>Send money to India at</h1>
          <div className={styles.ratePill}>{RATE_PREVIEW}</div>
          <p>No hidden charges. Guaranteed best FX rate</p>
        </section>

        <div className={styles.transferCard}>
          <div className={styles.currencyCol}>
            <span>You send</span>
            <strong>
              <img src={USFlag} alt="" aria-hidden />
              {TOI_WELCOME_BONUS}
            </strong>
          </div>
          <span className={styles.exchangeArrow} aria-hidden>
            →
          </span>
          <div className={`${styles.currencyCol} ${styles.currencyRight}`}>
            <span>Recipient gets</span>
            <strong>
              <img src={IndiaFlag} alt="" aria-hidden />
              {TOI_WELCOME_BONUS_INR}
            </strong>
          </div>
        </div>

        <section className={styles.rewardCard}>
          <div>
            <h2>
              Get <span>{TOI_WELCOME_BONUS} reward</span>
            </h2>
            <p>Your welcome bonus as a TOI reader</p>
          </div>
          <img src={rewardGiftIcon} alt="" aria-hidden />
        </section>

        <section className={styles.aboundSection}>
          <h2>What is Abound?</h2>
          <p>
            Abound is a remittance app that provides the guaranteed best FX
            rates for sending money.
          </p>
          <div className={styles.featureGrid}>
            {FEATURES.map((feature) => (
              <div className={styles.featureItem} key={feature.label}>
                <span className={styles.featureIcon}>
                  <img src={feature.icon} alt="" aria-hidden />
                </span>
                <strong>{feature.label}</strong>
              </div>
            ))}
          </div>
        </section>

        <section className={styles.comparisonSection}>
          <div className={styles.comparisonTitle}>
            <h2>Why Abound is Best?</h2>
            <p>
              For a <strong>{formatUsd(DEFAULT_USD_AMOUNT)}</strong> transfer
            </p>
          </div>

          <div className={styles.comparisonCard}>
            {hasComparisonData ? (
              <>
                <div className={styles.aboundRow}>
                  <div className={styles.provider}>
                    <AboundMark />
                    <strong>Abound</strong>
                  </div>
                  <div className={styles.aboundAmount}>
                    <strong>{formatTableInr(aboundRecipientAmount)}</strong>
                    <span>
                      <img src={trendArrow} alt="" aria-hidden />
                      {formatTableInr(extraAmount)} extra
                    </span>
                  </div>
                </div>

                {comparisonRows.map((row) => (
                  <div className={styles.comparisonRow} key={row.provider}>
                    <div className={styles.provider}>
                      {row.icon ? (
                        <img src={row.icon} alt="" aria-hidden />
                      ) : (
                        <span
                          className={styles.providerFallbackIcon}
                          aria-hidden
                        >
                          {row.provider.charAt(0)}
                        </span>
                      )}
                      <strong>{row.provider}</strong>
                    </div>
                    <span>{row.amount}</span>
                  </div>
                ))}
              </>
            ) : (
              <p className={styles.comparisonLoading}>Loading rates...</p>
            )}
          </div>
        </section>
      </main>

      <section className={styles.faqSection}>
        <h2>Frequently Asked Questions</h2>
        <div className={styles.faqList}>
          {FAQS.map((faq, index) => (
            <details
              key={faq.question}
              ref={(node) => {
                faqItemRefs.current[index] = node;
              }}
              className={styles.faqItem}
              open={openFaqIndex === index}
            >
              <summary
                onClick={(evt) => {
                  evt.preventDefault();
                  handleFaqToggle(index);
                }}
              >
                <span>{faq.question}</span>
                <span className={styles.chevron} aria-hidden />
              </summary>
              {faq.answer ? <p>{faq.answer}</p> : null}
            </details>
          ))}
        </div>
      </section>

      <div className={styles.footer}>
        <button className={styles.ctaBtn} onClick={handleContinue}>
          <span>Claim $50 Welcome Bonus</span>
          <span aria-hidden>→</span>
        </button>
        <TOIReminderSheet
          landingPageType={LANDING_PAGE_TYPE}
          onClaimNow={handleContinue}
          reminderSource={LANDING_PAGE_TYPE}
        />
      </div>
    </div>
  );
}

export default TOIAboundComparisonLandingPage;
